import { Link as RRDLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';
import { RouterLink } from '../Link';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${(props) => props.theme.color.bg.alt};
  box-shadow: 0 0 ${size(4)} rgba(0, 0, 0, 0.07);
`;

export const Header = styled.header`
  flex-shrink: 0;
  padding: ${size(2)} ${size(4)};
  border-bottom: 1px solid ${(props) => props.theme.color.border.main};

  svg {
    display: block;
    margin: 0 auto;
    max-height: 30px;
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

export const Footer = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  border-top: 1px solid ${(props) => props.theme.color.border.main};
`;

export const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  padding: ${size(1.5)} 0;
`;

export const Item = styled.li``;

const linkBase = css`
  display: flex;
  align-items: center;
  padding: ${size(1.5)} ${size(4)};
  font-family: ${(props) => props.theme.font.family.medium};

  &:hover {
    color: ${(props) => props.theme.color.brand.primary.main};
  }
`;

export const LinkBtn = styled(ButtonReset)`
  ${linkBase};
  width: 100%;
  text-align: left;
`;

// Navigation List (Level 1)

export const NavList = styled.ul`
  --main-navigation-nav-list-gutter-left: ${size(4)};
  --main-navigation-nav-list-popout: calc(var(--main-navigation-nav-list-gutter-left) / -2);

  list-style: none;
  padding: 0;
  margin: 0 var(--main-navigation-nav-list-popout);
  padding: ${size(2)};
`;

export const NavItem = styled.li`
  & + & {
    margin-top: ${size(0.5)};
  }
`;

// Styling shared between NavLink and GroupBtn
export const NavLinkBaseStyle = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${size(1.5)};
  width: 100%;
  padding: ${size(1.5)} ${size(1.5)} ${size(1.5)} var(--main-navigation-nav-list-gutter-left);
  border-radius: var(--main-navigation-element-border-radius);
  font-size: ${(props) => props.theme.font.size.regular};
  line-height: ${(props) => props.theme.font.lineHeight};
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
`;

export const NavLink = styled(RRDLink)<LinkProps>`
  ${NavLinkBaseStyle};
  color: currentColor;
  text-decoration: none;
  isolation: isolate;
  font-family: ${(props) => props.theme.font.family.medium};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${(props) => props.theme.color.brand.primary.alt};
    border-radius: inherit;
    opacity: 0;
    z-index: -1;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      &::before {
        opacity: 1;
      }
      color: ${(props) => props.theme.color.brand.primary.main};
    `}
`;

// Naigation Group (Level 1 + 2)

interface GroupProps {
  $isActive?: boolean;
}

export const Group = styled.div<GroupProps>`
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${(props) => props.theme.color.brand.primary.alt};
    border-radius: var(--main-navigation-element-border-radius);
    opacity: 0;
    z-index: -1;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      &::before {
        opacity: 1;
      }
    `}
`;

export const GroupBtn = styled(RouterLink)`
  ${NavLinkBaseStyle};
  text-align: left;
  font-weight: var(--font-weight-bold) !important;
  text-decoration: none !important;
  color: currentColor;
  &:hover {
    color: currentColor !important;
  }
`;

export const GroupBtnIcon = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: ${size(4)};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: ${size(0.5)};
  font-size: ${size(3)};
`;

export const GroupList = styled.ul`
  list-style: none;
  padding: var(--main-navigation-group-list-gutter);
  margin: 0;
`;

export const GroupItem = styled.li``;

interface LinkProps {
  $isActive?: boolean;
  $deprecated?: boolean;
}

export const GroupLink = styled(RRDLink)<LinkProps>`
  --main-navigation-group-link-gutter-right: calc(
    ${size(4)} - var(--main-navigation-group-list-gutter)
  );

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${size(1.5)};
  padding: 5px 30px;
  border-radius: calc(
    var(--main-navigation-element-border-radius) - var(--main-navigation-group-list-gutter)
  );
  color: var(--main-navigation-fg);
  font-size: ${(props) => props.theme.font.size.small};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  isolation: isolate;

  ${({ $deprecated }) =>
    $deprecated &&
    css`
      color: var(--c-text-muted);
      text-decoration: line-through;
    `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${(props) => props.theme.color.brand.primary.alt};
    border-radius: inherit;
    opacity: 0;
    z-index: -1;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      &::before {
        opacity: 1;
      }
      color: ${(props) => props.theme.color.brand.primary.main};
    `}
`;
